// Tableau de dispatch du jour (transport, lot T5a) : Patron et dispatcheurs seulement (route /dispatch, droit
// « dispatch »). Tout vient de GET /api/dispatch/board (backend/dispatch.py) : courses dont la prise en charge tombe
// ce jour-là (heure des Réglages), par heure, avec leur état et leur prix ; compteurs ; bandeau des chauffeurs (tous
// les chauffeurs actifs) et des véhicules. Mise à jour en direct (temps réel des courses) et toutes les 30 s : « libre
// / en course » change aussi avec l'heure, sans aucun clic.
// Étape B2 : menu « Attribuer » sur chaque course pas encore commencée (chauffeur seulement ; le véhicule se choisit
// dans la fenêtre de course).
// Lot T5b (indisponibilités) : bouton « Marquer indisponible… » sur chaque carte chauffeur, pastille « Indisponible », bloc
// « Indisponibilités à venir » (modifier, supprimer) ; lignes « Chauffeur indisponible » (attribuée quand même) et
// « Chauffeur indisponible (avant : …) » (à réattribuer) ; menu « Attribuer » : un chauffeur indisponible est
// choisissable après confirmation.
// Lot T5b, étape B2 : /dispatch?day=AAAA-MM-JJ&course=<id> (clic sur une alerte de la cloche) ouvre ce jour-là, retire le
// filtre, fait défiler jusqu'à la course et la met en évidence 3 s ; course absente ce jour-là : « Course introuvable… ».
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, todayIn, usePoll, useWebSocket } from "@/lib/helpers";
import { DISPATCH_STATES, DISPATCH_COUNTERS, NO_SHOW_BAR, STEP_CLS, dayIn, requestsOf, requestText } from "@/lib/ride";
import { unavShort, unavText, untilText } from "@/lib/unavailability";
import StatusBadge from "@/components/StatusBadge";
import UnavailabilityDialog from "@/components/UnavailabilityDialog";
import { Abandon, useUnavailableConfirm } from "@/components/UnavailableConfirm";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, ChevronDown, CarFront, UserRound, UserPlus, UserMinus, Check, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { WaitingOffer } from "@/components/RideParts";

// Décale une date AAAA-MM-JJ de n jours (calcul en UTC : aucun effet de fuseau ni d'heure d'été), comme le Journal.
const shiftDay = (day, n) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const longDate = (day) => {
  const s = new Date(`${day}T12:00:00Z`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const pill = "inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap";
const GREY = "text-muted-foreground bg-muted";

// État d'une course : « Pas encore reçue » (+ « Attribuée à 09:05 »), étape en cours, « Client absent » en rouge.
function StatePills({ r, hour }) {
  if (r.state === "cancelled") return <StatusBadge status="cancelled" />;
  const s = DISPATCH_STATES[r.state];
  if (r.step === "no_show") return <span className={cn(pill, STEP_CLS.no_show)} data-testid={`dispatch-ride-state-${r.id}`}>Client absent</span>;
  return (
    <>
      <span className={cn(pill, r.state === "in_progress" ? STEP_CLS[r.step] : s.cls)} data-testid={`dispatch-ride-state-${r.id}`}>
        {r.state === "in_progress" ? r.step_label : r.state === "received" && r.received_at ? `Reçue à ${hour(r.received_at)}` : s.label}
      </span>
      {r.state === "not_received" && r.assigned_at && (
        <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`dispatch-ride-assigned-${r.id}`}>Attribuée à {hour(r.assigned_at)}</span>
      )}
    </>
  );
}

// Menu « Attribuer » (étape B2) : tous les chauffeurs actifs, libres en haut ; occupés grisés avec la raison (leur course
// sur ce créneau) ; « Retirer le chauffeur ». Même règle que l'enregistrement (GET /dispatch/drivers) : un chauffeur
// grisé serait refusé. L'enregistrement passe par PATCH /missions/{id} avec la course réduite à type, client, début, fin
// et chauffeur (PATCH exige ces quatre champs ; les autres, absents, ne changent pas : prix, véhicule, notes…).
// Seulement sur une course pas encore commencée : une course en cours se réattribue dans la fenêtre de course.
const ASSIGNABLE = ["to_assign", "not_received", "received"];

function AssignMenu({ r, hour, tz, day, onDone }) {
  const [drivers, setDrivers] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const { confirmDialog, withConfirm } = useUnavailableConfirm();
  const ouvrir = (open) => {
    if (!open) return;
    setDrivers(null);
    api.get("/dispatch/drivers", { params: { mission_id: r.id } }).then((x) => setDrivers(x.data)).catch(() => setDrivers([]));
  };
  const enregistrer = async (employeeId, nom) => {
    setEnvoi(true);
    try {
      // Chauffeur indisponible : 409, puis « Attribuer quand même ? » (lot T5b).
      await withConfirm((force) => api.patch(`/missions/${r.id}`, { type: r.type, client_id: r.client_id, start: r.start, end: r.end,
        employee_id: employeeId }, { params: force ? { force_unavailable: true } : {} }));
      toast.success(employeeId ? `Course attribuée à ${nom}` : "Chauffeur retiré");
      onDone();
    } catch (e) {
      if (!(e instanceof Abandon)) toast.error(apiError(e.response?.data?.detail) || "Attribution impossible");
    } finally {
      setEnvoi(false);
    }
  };
  const raison = (d) => {
    const c = d.conflicts[0];
    if (!c) {
      const u = (d.unavailable || [])[0]; // indisponible (lot T5b) : choisissable, avec confirmation
      return u ? `Indisponible ${unavShort(u, tz, day)}${u.reason ? ` · ${u.reason}` : ""}` : "";
    }
    const autres = d.conflicts.length - 1;
    return `Pris ${hour(c.start)} → ${hour(c.end)}${c.route ? ` · ${c.route}` : ""}${autres > 0 ? ` (+${autres})` : ""}`;
  };
  return (
    <>
    {confirmDialog}
    <DropdownMenu onOpenChange={ouvrir}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={envoi} data-testid={`dispatch-assign-${r.id}`}>
          <UserPlus className="h-3.5 w-3.5" /> {r.employee_id ? "Changer" : "Attribuer"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-w-[calc(100vw-2rem)]" data-testid={`dispatch-assign-menu-${r.id}`}>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Chauffeur · {hour(r.start)} → {hour(r.end)}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {drivers === null && <div className="px-2 py-1.5 text-sm text-muted-foreground">Chargement…</div>}
        {drivers && drivers.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">Aucun chauffeur actif.</div>}
        {(drivers || []).map((d) => (
          <DropdownMenuItem key={d.id} disabled={d.conflicts.length > 0 || d.current} onSelect={() => enregistrer(d.id, d.name)}
            className="flex-col items-start gap-0" data-testid={`dispatch-assign-option-${d.id}`} data-free={d.free ? "true" : "false"}>
            <span className="font-medium inline-flex items-center gap-1.5">{d.current && <Check className="h-3.5 w-3.5" />}{d.name}{d.current ? " (actuel)" : ""}</span>
            {!d.free && <span className="text-xs text-muted-foreground" data-testid={`dispatch-assign-reason-${d.id}`}>{raison(d)}</span>}
          </DropdownMenuItem>
        ))}
        {r.employee_id && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => enregistrer("", "")} className="text-red-700 dark:text-red-300" data-testid={`dispatch-assign-remove-${r.id}`}>
              <UserMinus /> Retirer le chauffeur
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

function RideRow({ r, hour, day, tz, cur, onChange, focused }) {
  const cancelled = r.state === "cancelled";
  const autreJour = r.end && dayIn(r.end, tz) !== day;
  const demandes = requestsOf(r);
  return (
    <div className={cn("px-4 md:px-5 py-3 flex items-start gap-3 flex-wrap border-l-4 transition-colors duration-700 scroll-mt-24", cancelled && "opacity-60",
      focused && "bg-accent/15 ring-2 ring-inset ring-accent")} data-focused={focused ? "true" : undefined}
      style={{ borderLeftColor: cancelled ? "transparent" : r.step === "no_show" ? NO_SHOW_BAR : DISPATCH_STATES[r.state]?.bar }}
      data-testid={`dispatch-ride-${r.id}`} data-state={r.state}>
      <div className={cn("w-14 shrink-0 tabular-nums", cancelled && "line-through")}>
        <div className="font-display font-bold">{hour(r.start)}</div>
        <div className="text-xs text-muted-foreground" title={autreJour ? "Se termine le lendemain" : undefined}>→ {hour(r.end)}{autreJour ? " +1 j" : ""}</div>
      </div>
      <div className={cn("flex-1 min-w-[180px] space-y-0.5", cancelled && "line-through")}>
        <div className="font-semibold leading-snug">
          {r.client_name || "—"}
          {r.passenger_name && <span className="font-normal text-muted-foreground"> · {r.passenger_name}</span>}
        </div>
        {r.route && <div className="text-sm text-muted-foreground break-words">{r.route}</div>}
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5" />
            {r.employee_name || <span className="italic">Sans chauffeur</span>}</span>
          {/* Lot T5b : attribuée quand même à un chauffeur indisponible ; ou chauffeur retiré par une indisponibilité. */}
          {r.driver_unavailable && !cancelled && <span className={UNAV_TEXT} data-testid={`dispatch-ride-unavailable-${r.id}`}>Chauffeur indisponible</span>}
          {r.released_from && r.state === "to_assign" && (
            <span className={UNAV_TEXT} data-testid={`dispatch-ride-released-${r.id}`}>Chauffeur indisponible (avant : {r.released_from})</span>
          )}
          {r.vehicle_name
            ? <span className="inline-flex items-center gap-1"><CarFront className="h-3.5 w-3.5" />{r.vehicle_name}</span>
            : <span className={cn(pill, GREY, "py-0.5")} data-testid={`dispatch-ride-novehicle-${r.id}`}>Sans véhicule</span>}
          {r.travel_ref && <span>{r.travel_ref}</span>}
          {demandes.length > 0 && <span>{demandes.map(requestText).join(" · ")}</span>}
        </div>
      </div>
      {/* Sur téléphone : pastilles sous le texte (alignées avec lui), prix à droite. */}
      <div className="flex items-center gap-2 flex-wrap justify-end ml-auto max-md:basis-full max-md:justify-start max-md:pl-[4.25rem]">
        <StatePills r={r} hour={hour} />
        <WaitingOffer m={r} onDone={onChange} compact />
        {ASSIGNABLE.includes(r.state) && <AssignMenu r={r} hour={hour} tz={tz} day={day} onDone={onChange} />}
        <span className={cn("font-display font-bold min-w-20 text-right max-md:ml-auto", cancelled && "line-through text-muted-foreground font-normal")}
          data-testid={`dispatch-ride-price-${r.id}`}>{money(r.price_ttc, cur)}</span>
      </div>
    </div>
  );
}

// Bandeau qui défile de gauche à droite (téléphone compris) ; la page, elle, ne défile jamais en largeur.
function Strip({ title, count, children, testid }) {
  return (
    <section className="space-y-2 min-w-0" data-testid={testid}>
      <div className="text-xs uppercase tracking-overline text-muted-foreground">{title}{count > 0 && <span className="ml-1.5 normal-case tracking-normal">· {count}</span>}</div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x" tabIndex={0} aria-label={title}>{children}</div>
    </section>
  );
}
const card = "snap-start shrink-0 w-60 bg-card border border-border rounded-2xl shadow-card p-3.5 space-y-1.5";
const FREE = "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300";
const BUSY = "text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300";
const LATE = "text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300"; // même rouge que la ligne « En retard »
const UNAV = "text-slate-700 bg-slate-200 dark:bg-slate-800 dark:text-slate-200"; // indisponible (lot T5b)
const UNAV_TEXT = "font-medium text-red-700 dark:text-red-300";

// Course actuelle pas encore commencée et dont l'heure est passée (elle est dans la liste `late` du serveur) : la
// pastille dit « En retard » en rouge ; « En course · … » (bleu) seulement à partir de « En route ».
const currentLate = (x) => Boolean(x.current && (x.late || []).some((l) => l.id === x.current.id));

// Courses pas encore commencées dont l'heure est passée : jamais retirées du bandeau, affichées en premier.
function LateLines({ late, hour, testid }) {
  return (late || []).map((x) => (
    <div key={x.id} className="text-xs font-medium text-red-700 dark:text-red-300 truncate" data-testid={`${testid}-${x.id}`}>
      En retard : {hour(x.start)} · {x.route || "—"}
    </div>
  ));
}

// Pastille du chauffeur aujourd'hui (ordre du serveur) : en course (commencée) > indisponible > en retard > pris > libre.
function driverPill(d, tz, today) {
  if (d.status === "unavailable") return [UNAV, `Indisponible ${untilText(d.unavailable_until, tz, today)}`];
  if (d.status !== "busy") return [FREE, "Libre"];
  return currentLate(d) ? [LATE, "En retard"] : [BUSY, `En course · ${d.current.step_label}`];
}

function DriverCard({ d, isToday, hour, tz, day, onUnavailable }) {
  const [cls, texte] = isToday ? driverPill(d, tz, day) : [];
  // Indisponibilités du jour affiché ; aujourd'hui, celle en cours est déjà dans la pastille.
  const indispos = (d.unavailable || []).filter((u) => !(isToday && d.status === "unavailable" && u.end === d.unavailable_until));
  return (
    <div className={cn(card, "flex flex-col")} data-testid={`dispatch-driver-${d.id}`}>
      <div className="font-semibold truncate">{d.name}</div>
      {isToday ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(pill, cls)} data-testid={`dispatch-driver-status-${d.id}`}>{texte}</span>
          </div>
          <LateLines late={d.late} hour={hour} testid={`dispatch-driver-late-${d.id}`} />
          {/* Course en cours ; une course pas commencée est déjà écrite « En retard » juste au-dessus. */}
          {d.current && d.current.step !== "" && <div className="text-xs text-muted-foreground truncate">{hour(d.current.start)} → {hour(d.current.end)} · {d.current.route || "—"}</div>}
          <div className="text-xs text-muted-foreground truncate" data-testid={`dispatch-driver-next-${d.id}`}>
            {d.next ? `Prochaine : ${hour(d.next.start)} · ${d.next.route || "—"}` : "Aucune autre course aujourd'hui"}
          </div>
        </>
      ) : (
        <div className="text-xs text-muted-foreground" data-testid={`dispatch-driver-day-${d.id}`}>
          {d.rides_count === 0 ? "Aucune course" : `${d.rides_count} course${d.rides_count > 1 ? "s" : ""} · 1re prise en charge ${hour(d.first.start)}`}
          {d.first && <div className="truncate">{d.first.route || ""}</div>}
        </div>
      )}
      {indispos.map((u) => (
        <div key={u.id} className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate" title={u.reason || undefined} data-testid={`dispatch-driver-unav-${d.id}-${u.id}`}>
          Indisponible {unavShort(u, tz, day)}{u.reason ? ` · ${u.reason}` : ""}
        </div>
      ))}
      {/* Zone de clic de 28 px de haut, même apparence que les liens « + Nouveau client » (lot 22). */}
      <button type="button" onClick={() => onUnavailable(d)} className="mt-auto self-start text-xs text-accent inline-flex items-center gap-1 py-1.5 -mb-1.5"
        data-testid={`dispatch-driver-unav-btn-${d.id}`}>
        <CalendarOff className="h-3.5 w-3.5" /> Marquer indisponible…
      </button>
    </div>
  );
}

// Bloc repliable « Indisponibilités à venir (n) » : en cours et à venir, tous chauffeurs ; modifier, supprimer.
function Upcoming({ items, tz, onEdit, onDelete }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <section className="bg-card border border-border rounded-2xl shadow-card min-w-0" data-testid="dispatch-unavs">
      <button type="button" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert}
        className="w-full px-5 py-3.5 flex items-center justify-between gap-3 text-left" data-testid="dispatch-unavs-toggle">
        <span className="text-xs uppercase tracking-overline text-muted-foreground">
          Indisponibilités à venir<span className="ml-1.5 normal-case tracking-normal">({items.length})</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", ouvert && "rotate-180")} />
      </button>
      {ouvert && (
        <div className="border-t border-border divide-y divide-border">
          {items.length === 0 && <p className="px-5 py-4 text-sm text-muted-foreground">Aucune indisponibilité à venir.</p>}
          {items.map((u) => (
            <div key={u.id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`dispatch-unav-${u.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold">{u.employee_name || "—"}</div>
                <div className="text-sm text-muted-foreground break-words">{unavText(u, tz)}{u.reason ? ` · ${u.reason}` : ""}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(u)} data-testid={`dispatch-unav-edit-${u.id}`}>Modifier</Button>
                <Button size="sm" variant="outline" className="h-8 text-red-700 dark:text-red-300" onClick={() => onDelete(u)} data-testid={`dispatch-unav-delete-${u.id}`}>Supprimer</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function VehicleCard({ v, isToday, hour }) {
  return (
    <div className={card} data-testid={`dispatch-vehicle-${v.id}`}>
      <div className="font-semibold truncate" title={v.name}>{v.name}</div>
      {isToday ? (
        <>
          <span className={cn(pill, v.status !== "busy" ? FREE : currentLate(v) ? LATE : BUSY)} data-testid={`dispatch-vehicle-status-${v.id}`}>
            {v.status !== "busy" ? "Libre" : currentLate(v) ? "En retard" : `Pris jusqu'à ${hour(v.until)}`}
          </span>
          <LateLines late={v.late} hour={hour} testid={`dispatch-vehicle-late-${v.id}`} />
          <div className="text-xs text-muted-foreground truncate">
            {v.status === "busy" ? (v.current.employee_name || "Sans chauffeur") : v.next ? `Prochaine : ${hour(v.next.start)}${v.next.employee_name ? ` · ${v.next.employee_name}` : ""}` : "Aucune autre course aujourd'hui"}
          </div>
        </>
      ) : (
        <div className="text-xs text-muted-foreground space-y-0.5" data-testid={`dispatch-vehicle-day-${v.id}`}>
          {v.chains.length === 0 ? "Libre toute la journée" : v.chains.map((c) => (
            <div key={c.start} className="truncate">
              {hour(c.start)} → {hour(c.end)}{(() => { const n = [...new Set(c.rides.map((x) => x.employee_name).filter(Boolean))]; return n.length ? ` · ${n.join(", ")}` : ""; })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dispatch() {
  const { settings } = useSettings();
  const cur = settings?.currency || "EUR";
  const tz = settings?.timezone || "Europe/Monaco";
  const [day, setDay] = useState(() => todayIn(tz));
  const [data, setData] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [unavs, setUnavs] = useState([]); // indisponibilités en cours et à venir (lot T5b)
  const [unavDialog, setUnavDialog] = useState({ open: false, driverId: "", edit: null });
  const [toDelete, setToDelete] = useState(null);
  // Clic sur une alerte (lot T5b) : course à montrer, puis course mise en évidence pendant 3 s.
  const [params, setParams] = useSearchParams();
  const [cible, setCible] = useState(null); // { day, id }
  const [focus, setFocus] = useState("");
  useEffect(() => {
    const d = params.get("day"), id = params.get("course");
    if (!d && !id) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(d || "")) setDay(d);
    if (id) { setCible({ day: /^\d{4}-\d{2}-\d{2}$/.test(d || "") ? d : null, id }); setFiltre(""); }
    setParams({}, { replace: true }); // adresse nettoyée : recharger la page ne refait pas l'effet
  }, [params, setParams]);

  // Seule la réponse du jour affiché compte : ouverte sur un autre jour (adresse, lien d'une alerte), la page a aussi
  // demandé aujourd'hui, et cette réponse peut arriver après (lot T2 ; avant : « Chargement… » jusqu'au rechargement).
  const jourDemande = useRef(day);
  jourDemande.current = day;
  const load = useCallback(() => {
    api.get("/dispatch/board", { params: { day } }).then((r) => { if (day === jourDemande.current) setData(r.data); }).catch(() => {});
    api.get("/unavailabilities").then((r) => setUnavs(r.data)).catch(() => {});
  }, [day]);
  useEffect(() => { load(); }, [load]);
  // Temps réel : un changement de course recharge le tableau (regroupé : plusieurs signaux d'affilée = un seul appel).
  const attente = useRef(null);
  useWebSocket((d) => {
    const t = String(d?.type || "");
    if (!t.startsWith("mission") && t !== "unavailability_changed") return;
    clearTimeout(attente.current);
    attente.current = setTimeout(() => load(), 300);
  });
  useEffect(() => () => clearTimeout(attente.current), []);
  usePoll(() => load(), 30000);

  // La course demandée, une fois le bon jour chargé : défiler jusqu'à elle et la mettre en évidence 3 s.
  useEffect(() => {
    if (!cible || !data || (cible.day && data.day !== cible.day)) return;
    setCible(null);
    if (!data.rides.some((r) => r.id === cible.id)) {
      toast.error("Course introuvable ce jour-là (déplacée ou supprimée).");
      return;
    }
    setFocus(cible.id);
    requestAnimationFrame(() => {
      const reduit = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      document.querySelector(`[data-testid="dispatch-ride-${cible.id}"]`)?.scrollIntoView({ behavior: reduit ? "auto" : "smooth", block: "center" });
    });
  }, [cible, data]);
  useEffect(() => {
    if (!focus) return undefined;
    const t = setTimeout(() => setFocus(""), 3000);
    return () => clearTimeout(t);
  }, [focus]);

  const hour = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: tz }) : "");
  const today = todayIn(tz);
  const ok = data && data.day === day;
  const isToday = ok && data.day === data.today;
  const rides = ok ? data.rides.filter((r) => !filtre || r.state === filtre) : [];
  const etiquette = day === today ? "Aujourd'hui" : day === shiftDay(today, 1) ? "Demain" : day === shiftDay(today, -1) ? "Hier" : "";
  const supprimer = async () => {
    const u = toDelete;
    setToDelete(null);
    try { await api.delete(`/unavailabilities/${u.id}`); toast.success("Indisponibilité supprimée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 min-w-0" data-testid="dispatch-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent" data-testid="dispatch-day-label">{[etiquette, longDate(day)].filter(Boolean).join(" · ")}</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Dispatch</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="icon" variant="outline" onClick={() => setDay((d) => shiftDay(d, -1))} title="Jour précédent" aria-label="Jour précédent" data-testid="dispatch-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label="Jour affiché" className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="dispatch-day" />
          <Button size="icon" variant="outline" onClick={() => setDay((d) => shiftDay(d, 1))} title="Jour suivant" aria-label="Jour suivant" data-testid="dispatch-next"><ChevronRight className="h-4 w-4" /></Button>
          {day !== today && <Button variant="outline" onClick={() => setDay(today)} data-testid="dispatch-today">Aujourd'hui</Button>}
        </div>
      </div>

      {!ok && <p className="text-muted-foreground">Chargement…</p>}
      {ok && (
        <>
          {/* Compteurs : un clic filtre la liste, un 2e clic la remet en entier. Les courses annulées ne comptent pas.
              5 côte à côte à partir de 1280 px, 3 par ligne dès 768 px, 2 sur téléphone ; chiffres alignés en bas
              (« Pas encore reçues » peut tenir sur deux lignes). */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {DISPATCH_COUNTERS.map((k) => {
              const s = DISPATCH_STATES[k];
              const actif = filtre === k;
              return (
                <button key={k} type="button" onClick={() => setFiltre(actif ? "" : k)} aria-pressed={actif}
                  className={cn("text-left flex flex-col justify-between gap-1 bg-card border rounded-2xl shadow-card p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    actif ? "border-foreground ring-1 ring-foreground" : "border-border")}
                  data-testid={`dispatch-counter-${k}`}>
                  <div className="text-xs uppercase tracking-overline text-muted-foreground">{s.counter}</div>
                  <div className="font-display font-black text-3xl tabular-nums" style={{ color: s.tint }} data-testid={`dispatch-counter-${k}-value`}>{data.counters[k]}</div>
                </button>
              );
            })}
          </div>

          <Strip title={isToday ? "Chauffeurs en ce moment" : "Chauffeurs ce jour-là"} count={data.drivers.length} testid="dispatch-drivers">
            {data.drivers.length === 0 && <p className="text-sm text-muted-foreground">Aucun chauffeur actif.</p>}
            {data.drivers.map((d) => <DriverCard key={d.id} d={d} isToday={isToday} hour={hour} tz={tz} day={day}
              onUnavailable={(x) => setUnavDialog({ open: true, driverId: x.id, edit: null })} />)}
          </Strip>
          <Strip title={isToday ? "Véhicules en ce moment" : "Véhicules ce jour-là"} count={data.vehicles.length} testid="dispatch-vehicles">
            {data.vehicles.length === 0 && <p className="text-sm text-muted-foreground">Aucun véhicule dans la flotte.</p>}
            {data.vehicles.map((v) => <VehicleCard key={v.id} v={v} isToday={isToday} hour={hour} />)}
          </Strip>
          {/* Sous les deux bandeaux (lot T5b), juste avant la liste des courses. */}
          <Upcoming items={unavs} tz={tz} onEdit={(u) => setUnavDialog({ open: true, driverId: u.employee_id, edit: u })} onDelete={setToDelete} />

          <div className="bg-card border border-border rounded-2xl shadow-card min-w-0 overflow-hidden" data-testid="dispatch-rides">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-overline text-muted-foreground">
                Courses par heure{filtre && <> · <span className="normal-case tracking-normal text-foreground">{DISPATCH_STATES[filtre].counter}</span></>}
              </div>
              {filtre
                ? <button type="button" className="text-xs underline text-muted-foreground hover:text-foreground" onClick={() => setFiltre("")} data-testid="dispatch-filter-clear">Tout afficher</button>
                : <span className="text-xs text-muted-foreground">{data.rides.length}</span>}
            </div>
            <div className="divide-y divide-border">
              {rides.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="dispatch-empty">{filtre ? "Aucune course dans cet état." : "Aucune course ce jour-là."}</p>}
              {rides.map((r) => <RideRow key={r.id} r={r} hour={hour} day={day} tz={tz} cur={cur} onChange={load} focused={focus === r.id} />)}
            </div>
          </div>
        </>
      )}
      <UnavailabilityDialog open={unavDialog.open} onOpenChange={(o) => setUnavDialog((x) => ({ ...x, open: o }))}
        drivers={(data?.drivers || []).map((d) => ({ id: d.id, name: d.name }))} driverId={unavDialog.driverId} edit={unavDialog.edit}
        day={day} tz={tz} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="unav-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{`Supprimer l'indisponibilité de ${toDelete?.employee_name || "ce chauffeur"} ?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `${unavText(toDelete, tz).replace(/^./, (c) => c.toUpperCase())}. ` : ""}Le chauffeur est prévenu. Les courses qui lui ont été retirées ne lui reviennent pas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unav-delete-no">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={supprimer} data-testid="unav-delete-yes">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
